/**
 * QuickSupplierDialog: alta rápida de proveedor en el maestro de la empresa
 * activa sin salir del Libro de IVA desde el botón junto al NIF.
 *
 * Inserta una fila en supplier_master (company_id de la empresa activa) y, tras
 * éxito, notifica al padre con la entrada creada para que aplique los valores
 * a la fila editada y refresque el mapa local del maestro.
 *
 * La tabla supplier_master tiene RLS que solo permite INSERT a admins. Si la
 * inserción falla por permisos, se muestra un toast explicativo.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GripHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { CatalogEntry } from "@/components/ExpenseAccountCombobox";
import { OPERATION_TYPE_OPTIONS, RETENTION_TYPE_OPTIONS, type SupplierMasterEntry, type SupplierOperationType, type SupplierRetentionType } from "@/lib/supplierMaster";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { expandAccountShortcut, inferAccountLength } from "@/lib/accountShortcut";

const normalizeNif = (nif: string): string =>
  (nif || "").replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");

export interface QuickSupplierCreated {
  taxId: string;
  accountCode: string;
  expenseAccount: string;
  description: string;
  postalCode: string;
  /** Entrada lista para fusionar en el Map<string, SupplierMasterEntry> local. */
  entry: SupplierMasterEntry;
}

interface Props {
  open: boolean;
  onClose: () => void;
  companyId: string | null | undefined;
  defaults: { taxId: string; name: string };
  catalog: CatalogEntry[];
  /** Se invoca tras una inserción correcta. */
  onCreated: (created: QuickSupplierCreated) => void;
  /** Renderiza dentro del editor padre para conservar foco, escritura y scroll del documento. */
  contained?: boolean;
}

const QuickSupplierDialog = ({
  open,
  onClose,
  companyId,
  defaults,
  catalog,
  onCreated,
  contained = false,
}: Props) => {
  const { toast } = useToast();
  const [taxId, setTaxId] = useState("");
  const [name, setName] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [expenseAccount, setExpenseAccount] = useState("");
  const [description, setDescription] = useState("");
  const [operationType, setOperationType] = useState<SupplierOperationType>("interior");
  const [retentionType, setRetentionType] = useState<SupplierRetentionType>("none");
  const [postalCode, setPostalCode] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Panel flotante arrastrable ──────────────────────────────
  // El panel no es un Dialog modal: así el visor de documento en segundo
  // plano sigue siendo interactivo (scroll, rotación) y esta ventana se
  // puede desplazar arrastrándola por la cabecera para no tapar los datos
  // que estás copiando desde la factura.
  const PANEL_W = 480;
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ dx: number; dy: number; bounds: DOMRect | null } | null>(null);

  // Centrar en la primera apertura y resetear formulario
  useEffect(() => {
    if (!open) return;
    setTaxId(normalizeNif(defaults.taxId || ""));
    setName((defaults.name || "").trim());
    setAccountCode("");
    setExpenseAccount("");
    setDescription("");
    setOperationType("interior");
    setPostalCode("");
    setSaving(false);
    setPos((prev) =>
      prev ?? {
        x: Math.max(16, (contained ? window.innerWidth * 0.9 : window.innerWidth) / 2 - PANEL_W / 2),
        y: contained ? 56 : Math.max(16, window.innerHeight * 0.12),
      },
    );
  }, [open, defaults.taxId, defaults.name, contained]);

  // Cerrar con Escape (equivalente al comportamiento del Dialog anterior)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  const startDrag = (e: React.PointerEvent) => {
    if (!pos) return;
    const target = e.target as HTMLElement;
    if (target.closest("button,input,textarea,select,[role='button'],[data-radix-select-trigger]")) return;
    const bounds = contained ? panelRef.current?.parentElement?.getBoundingClientRect() ?? null : null;
    dragRef.current = {
      dx: e.clientX - (bounds?.left ?? 0) - pos.x,
      dy: e.clientY - (bounds?.top ?? 0) - pos.y,
      bounds,
    };
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const maxX = (d.bounds?.width ?? window.innerWidth) - Math.min(panelRef.current?.offsetWidth ?? PANEL_W, PANEL_W);
      const maxY = (d.bounds?.height ?? window.innerHeight) - Math.min(panelRef.current?.offsetHeight ?? 40, 40);
      const nx = Math.min(
        Math.max(0, ev.clientX - (d.bounds?.left ?? 0) - d.dx),
        Math.max(0, maxX),
      );
      const ny = Math.min(
        Math.max(0, ev.clientY - (d.bounds?.top ?? 0) - d.dy),
        Math.max(0, maxY),
      );
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    e.preventDefault();
    e.stopPropagation();
  };

  const catalogMatch = useMemo(
    () => catalog.find((c) => c.account.trim() === expenseAccount.trim()),
    [catalog, expenseAccount],
  );

  const accountLength = useMemo(() => inferAccountLength(catalog.map((c) => c.account)), [catalog]);
  const expandAcc = (v: string) => expandAccountShortcut(v, accountLength);

  const canSave =
    !!companyId &&
    taxId.trim().length >= 8 &&
    name.trim().length > 0 &&
    accountCode.trim().length > 0;

  const handleSave = async () => {
    if (!companyId) {
      toast({
        title: "Sin empresa activa",
        description: "Aplica una empresa antes de crear un proveedor.",
        variant: "destructive",
      });
      return;
    }
    const cleanTaxId = normalizeNif(taxId);
    const cleanAccount = accountCode.trim();
    const cleanExpense = expenseAccount.trim();
    const cleanName = name.trim();
    // En supplier_master la columna `description` almacena la razón social del
    // proveedor (es el nombre que se muestra en el maestro y en exportaciones
    // A3). El campo "Descripción" del formulario sirve solo para alimentar la
    // descripción de la línea de la factura en curso.
    const cleanLineDescription =
      description.trim() || catalogMatch?.description || "";
    const cleanPostalCode = postalCode.replace(/\D/g, "").slice(0, 5);
    setSaving(true);
    try {
      const { error } = await supabase.from("supplier_master").insert({
        company_id: companyId,
        tax_id: cleanTaxId,
        account_code: cleanAccount,
        expense_account: cleanExpense,
        description: cleanName,
        operation_type: operationType,
        retention_type: retentionType,
        postal_code: cleanPostalCode || null,
        rules: [],
      });
      if (error) {
        const isPerms =
          error.code === "42501" ||
          /permission|row-level security/i.test(error.message || "");
        toast({
          title: isPerms
            ? "Solo los administradores pueden crear proveedores"
            : "No se pudo crear el proveedor",
          description: isPerms
            ? "Pide a un administrador que cree este proveedor en el maestro."
            : error.message,
          variant: "destructive",
        });
        setSaving(false);
        return;
      }
      const entry: SupplierMasterEntry = {
        accountCode: cleanAccount,
        expenseAccount: cleanExpense,
        description: cleanName,
        operationType,
        retentionType,
        postalCode: cleanPostalCode || undefined,
        rules: undefined,
      };
      onCreated({
        taxId: cleanTaxId,
        accountCode: cleanAccount,
        expenseAccount: cleanExpense,
        description: cleanLineDescription,
        postalCode: cleanPostalCode,
        entry,
      });
      // Notifica a otras pestañas (p.ej. la página Maestro de proveedores)
      // para que recarguen la tabla sin necesidad de F5.
      try {
        localStorage.setItem("supplier-master-updated", String(Date.now()));
        window.dispatchEvent(new CustomEvent("supplier-master-updated", {
          detail: { companyId, taxId: cleanTaxId },
        }));
      } catch {
        /* ignore quota / privacy errors */
      }
      toast({
        title: "Proveedor creado",
        description: `${name.trim()} · ${cleanTaxId} → ${cleanAccount}`,
      });
      onClose();
    } catch (e) {
      console.error("Quick supplier insert error:", e);
      toast({
        title: "Error al crear el proveedor",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open || !pos) return null;

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Nuevo proveedor en el maestro"
      data-quick-supplier-dialog="true"
      className={`${contained ? "absolute" : "fixed"} z-[100] rounded-lg border bg-background shadow-2xl pointer-events-auto`}
      style={{ left: pos.x, top: pos.y, width: `min(${PANEL_W}px, calc(100% - 32px))` }}
    >
      <div
        onPointerDown={startDrag}
        className="flex items-center gap-2 px-4 py-2 border-b bg-muted/40 rounded-t-lg cursor-move select-none"
      >
        <GripHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            Nuevo proveedor en el maestro
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            Arrastra para mover · el documento sigue interactivo
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 shrink-0"
          onClick={onClose}
          disabled={saving}
          title="Cerrar"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="qs-tax-id" className="text-xs">NIF / CIF</Label>
            <Input
              id="qs-tax-id"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value.toUpperCase())}
              className="h-8 font-mono text-xs"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="qs-account-code" className="text-xs">Cuenta proveedor</Label>
            <Input
              id="qs-account-code"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value.replace(/[^\d.]/g, ""))}
              onBlur={(e) => setAccountCode(expandAcc(e.target.value))}
              placeholder="400xxxxx / 410xxxxx (atajo: 410.9)"
              className="h-8 font-mono text-xs"
              inputMode="numeric"
              autoComplete="off"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="qs-name" className="text-xs">Razón social</Label>
          <Input
            id="qs-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-8 text-xs"
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="qs-expense" className="text-xs">
              Contrapartida por defecto
            </Label>
            <Input
              id="qs-expense"
              value={expenseAccount}
              onChange={(e) => setExpenseAccount(e.target.value.replace(/[^\d.]/g, ""))}
              onBlur={(e) => setExpenseAccount(expandAcc(e.target.value))}
              placeholder="opcional (atajo: 629.5)"
              list="qs-catalog"
              className="h-8 font-mono text-xs"
              inputMode="numeric"
              autoComplete="off"
            />
            <datalist id="qs-catalog">
              {catalog.slice(0, 200).map((c) => (
                <option key={c.account} value={c.account}>
                  {c.description}
                </option>
              ))}
            </datalist>
          </div>
          <div className="space-y-1">
            <Label htmlFor="qs-description" className="text-xs">Descripción línea factura</Label>
            <Input
              id="qs-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={catalogMatch?.description || "opcional"}
              className="h-8 text-xs"
              autoComplete="off"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="qs-optype" className="text-xs">Tipo de operación IVA</Label>
            <Select value={operationType} onValueChange={(v) => setOperationType(v as SupplierOperationType)}>
              <SelectTrigger id="qs-optype" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[110]">
                {OPERATION_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-xs">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="qs-postal" className="text-xs">Código postal (mod. 347)</Label>
            <Input
              id="qs-postal"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="p.ej. 28001"
              className="h-8 font-mono text-xs"
              inputMode="numeric"
              maxLength={5}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="qs-rettype" className="text-xs">Tipo de retención (modelo)</Label>
          <Select value={retentionType} onValueChange={(v) => setRetentionType(v as SupplierRetentionType)}>
            <SelectTrigger id="qs-rettype" className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[110]">
              {RETENTION_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground">
            Modelo 111: se traspasa sin impreso para tipificarlo en A3. Modelo 115
            (arrendamientos): impreso 180, clave dinerarias.
          </p>
        </div>



        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!canSave || saving}>
            {saving ? "Guardando…" : "Crear proveedor"}
          </Button>
        </div>
      </div>
    </div>
  );

  if (contained) return panel;

  return createPortal(panel, document.body);

};

export default QuickSupplierDialog;
